import { memo, useEffect } from 'react';
import type { Channel } from '@shared/types.ts';
import { subscribeToChannel, useTrack } from '@/stores/nowPlaying.ts';
import { secondaryLine } from '@/lib/slot.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { ChannelLogo } from '@/ui/ChannelLogo.tsx';

/**
 * One channel. Subscribes to its own metadata only while mounted, so the
 * server polls exactly the channels somebody is looking at.
 *
 * Note what is NOT rendered: "<title> by <artist>". Sports and talk channels
 * put a score in title and a matchup in artist — the Reds feed returns
 * "CIN 2 - LAD 3 • Final" / "Reds @ Dodgers". Treat the two as primary and
 * secondary text and let the content speak for itself.
 *
 * memo() is not premature here. A grid is sixty of these, and the things that
 * re-render the grid — playback status ticking loading → playing, a route
 * change — say nothing about any individual card. Without it, tuning one
 * channel re-renders fifty-nine cards that did not change.
 */
function ChannelCardImpl({
  channel, onPlay, isPlaying, favourite, autoFocus,
}: {
  channel: Channel;
  onPlay(channel: Channel): void;
  isPlaying: boolean;
  favourite?: boolean;
  autoFocus?: boolean;
}) {
  const track = useTrack(channel.id);
  const secondary = secondaryLine(track, channel.kind);

  useEffect(() => {
    if (!channel.capabilities.nowPlaying) return;
    return subscribeToChannel(channel.id);
  }, [channel.id, channel.capabilities.nowPlaying]);

  return (
    <Focusable
      id={`ch-${channel.id}`}
      onSelect={() => onPlay(channel)}
      autoFocus={autoFocus}
      className={`card ${isPlaying ? 'playing' : ''}`}
    >
      <div className="logo">
        <ChannelLogo channel={channel} className="logo-inner" />
        {isPlaying && <div className="bars"><i /><i /><i /></div>}
        {/* Top-left, away from the playing bars in the opposite corner. */}
        {favourite && <div className="star" aria-label="Favorite">★</div>}
      </div>

      <div className="meta">
        <div className="title">{channel.name}</div>
        {track
          ? (
            <div className="track">
              <div className="t1">{track.title}</div>
              {secondary && <div className="t2">{secondary}</div>}
            </div>
          )
          : <div className="track muted">{channel.categories[0] ?? ''}</div>}
      </div>
    </Focusable>
  );
}

export const ChannelCard = memo(ChannelCardImpl);
